import React, {useState} from "react";
import {DialogContent, DialogTitle} from "@mui/material";
import {Col, Row} from "reactstrap";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import {ReactComponent as Excel} from "../../../../assets/images/export.svg";
import Form from "../../base/form";
import {exportCSVFile, formatMoney} from "../../../../core/services/utils/utils";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import {MuiRFF, Yup} from '../../form';
import DateUtils from "../../../../core/services/date-utils";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import ToastService from "../../../../core/services/toast";
import useCachedPriceLevelsDictionaryController from "../../../hooks/use-price-level-dictionary";

const formValues = {
    startDate: 'startDate',
    endDate: 'endDate',
}

const schema = Yup.object().shape({
    [formValues.startDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
        .maxDate(Yup.ref(formValues.endDate), ValidateMessages.lessThan("End Date"))
        .nullable(),
    [formValues.endDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
        .minDate(Yup.ref(formValues.startDate), ValidateMessages.greaterThan("Start Date"))
        .nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);


const ExportProductPricesDialog = ({open, close, partNo, partId, livePrices}) => {
    const [exporting, setExporting] = useState(false);
    const isMounted = useIsMounted();
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();

    /**
     * Exports the data with live price filter values
     */
    const exportLivePrices = () => exportData({
        [formValues.startDate]: DateUtils.UTCNow(),
        [formValues.endDate]: DateUtils.UTCNow(),
        live: true,
    }, true)

    /**
     * Exports the data with past year price filter values
     */
    const exportThisYearPrices = () => exportData({
        [formValues.startDate]: DateUtils.UTCNow().startOf('year'),
        [formValues.endDate]: DateUtils.UTCNow(),
    })

    /**
     * Generates csv report for the price levels of the part:
     * - Calls the api for getting the list of price levels of the part
     * - Maps the result to the desired data structure and groups them by the title of the price level
     * - Sorts the data in each group based on the date
     * - Add the operator as the first property of the final object passed to operation for creating the csv file
     *
     * @param {boolean} isLive
     * @param {any} values form values
     */
    const exportData = async (values, isLive = false) => {
        if (isLive) {
            const title = `Live Prices of ${partNo ?? ''} on ${DateUtils.localNow().format(DateUtils.dateFormat)}`;
            const content = livePrices?.map((item, index) => ({
                "": index + 1,
                "Price Level": item.priceLevel?.title,
                "Price": formatMoney(item?.price),
            }))
            if (!content.length) {
                setExporting(false);
                ToastService.warning({message: 'There is no live price for this product.'});
                return;
            }
            exportCSVFile(content, title);
            return;
        }
        setExporting(true);
        const forApi = {
            partId: partId,
            startDate: DateUtils.UTCToServer(values[formValues.startDate]).format(DateUtils.withoutOffsetFormat),
            endDate: DateUtils.UTCToServer(values[formValues.endDate]).format(DateUtils.withoutOffsetFormat),
        }

        const [response, priceLevelDict] = await Promise.all([
            Api.generateReportOfPartPriceLevels(forApi),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted())
            return;
        if (!response?.resultFlag) {
            setExporting(false);
            return;
        }
        const title = `Prices of ${partNo ?? ''}`;
        const content = response?.data?.map((price, index) => ({
            "": index + 1,
            "Old Price": formatMoney(price?.oldPrice),
            "New Price": formatMoney(price?.newPrice),
            "Submitted Date & Time": DateUtils.serverToLocal(price?.submittedDate).format(DateUtils.dateTimeFormat),
            "Operator": price?.operatorName,
            "Price Level": priceLevelDict?.find(item => item.priceLevelId === price?.priceLevel?.id)?.title ?? price?.priceLevel?.title,
        }))
        if (!content.length) {
            setExporting(false);
            ToastService.warning({title: "No Data Found", message: 'No data found for the selected period'});
            return;
        }
        exportCSVFile(content, title);
        setExporting(false);
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={close}
                maxWidth={'md'}
            >
                <DialogTitle>
                    <Row xs={12} className='no-gutters justify-content-center w-100'>
                        <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                            Convert to Excel
                        </p>
                    </Row>
                </DialogTitle>
                <DialogContent dividers className={''}>
                    <div className={'d-flex flex-wrap mt-2'}>
                        <Button
                            disabled={!open || exporting}
                            className='mr-2 mb-2'
                            variant={'outlined'}
                            color={'primary'}
                            onClick={exportLivePrices}
                        >
                            <div className={'icon-button-icon-container background-transparent mr-2'}>
                                <Excel/>
                            </div>
                            <p>
                                {
                                    exporting
                                        ? 'Exporting...'
                                        : "Live Price"
                                }
                            </p>
                        </Button>
                        <Button
                            disabled={!open || exporting}
                            className='mr-2 mb-2'
                            variant={'outlined'}
                            color={'primary'}
                            onClick={exportThisYearPrices}
                        >
                            <div className={'icon-button-icon-container background-transparent mr-2'}>
                                <Excel/>
                            </div>
                            <p>
                                {
                                    exporting
                                        ? 'Exporting...'
                                        : "This Year"
                                }
                            </p>
                        </Button>
                    </div>
                    <p className={'mt-4 font-weight-semi-bold black text-md'}>
                        Choose the period
                    </p>
                    <Form
                        className='w-100 mt-3'
                        onSubmit={exportData}
                        validate={validate}
                        initialValues={{}}
                        render={({values}) => {
                            return (
                                <div className={'d-flex flex-wrap w-100 align-items-center'}>
                                    <Col xs={12} md={6} lg={'auto'} className={'mb-4 flex-lg-grow-1'}>
                                        <MuiRFF.DatePicker
                                            name={formValues.startDate}
                                            label={"Start Date"}
                                            required={required[formValues.startDate]}
                                            maxDate={values[formValues.endDate]}
                                            disableFuture
                                        />
                                    </Col>
                                    <Col xs={12} md={6} lg={'auto'} className={'mb-4 flex-lg-grow-1'}>
                                        <MuiRFF.DatePicker
                                            name={formValues.endDate}
                                            label={"End Date"}
                                            required={required[formValues.endDate]}
                                            minDate={values[formValues.startDate]}
                                            disableFuture
                                        />
                                    </Col>
                                    <Col xs={12} lg={'auto'} className={'mb-4 d-flex'}>
                                        <Button
                                            disabled={exporting}
                                            className='px-6 py-2-5 m-auto'
                                            type={'submit'}
                                            variant={'contained'}
                                            color={'primary'}
                                        >
                                            {exporting ? 'Generating...' : 'Generate'}
                                        </Button>
                                    </Col>
                                </div>
                            );
                        }}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}

export default ExportProductPricesDialog;
